import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import {
  ExportsDataTypes,
  ExportsFormats,
  GanttFilter,
  GanttSort,
  Preferences,
  RetrieveWorkLoadsFilter,
  Sort as S,
} from "@planzone/dto";
import {
  useAuthentication,
  useEditPreferences,
  useExportData,
  useRetrieveConstraints,
  useRetrieveCurrentPlanzone,
  useRetrieveOptimizedGantt,
  useRetrievePreferences,
  useRetrieveWbsMeta,
  useRetrieveWorkLoads,
} from "@planzone/view-models";

import { events } from "@planzone/events";
import { useMessage } from "@components/useMessage";
import { Wbs } from "@scenes/wbs";
import { TreeToggle } from "@common/treeToggle";
import { Flag, InfiniteScrollRoot } from "@common/infiniteScroll";
import { Props } from "./types";
import { Graph } from "./graph";
import { getDates, isToday, useRange, useResources } from "./utils";
import { createHoverStore, HoverProvider } from "@common/elementHover";
import { useViews, Views, views as defaultViews } from "./api/views";
import { useWorkloads } from "./api/workloads";
import { useDelays } from "./api/delays";
import { useDerivations } from "./api/derivations";
import { Header } from "./header";
import { WbsHeader } from "./wbsHeader";
import { DetailsView } from "./detailsView";
import { WorkLoads } from "./workloads";
import { Resources } from "./resources";
import { Calendar } from "./calendar";
import { ScrollBar, ScrollRoot, ScrollZone } from "./scroll";
import { ZoomMenu } from "./zoomMenu";
import { ExportButton } from "./exportButton";
import { Context } from "./context";
import { createScrollPosStore } from "./scrollPosStore";
import { ScrollPosProvider } from "./scrollPosContext";
import styles from "./styles.scss";

// Latest-ref wrapper: a permanently-stable function identity that always
// calls the newest version of `fn`. The api hooks (useViews/useWorkloads/…)
// recreate their setters on every render; wrapping them means the memoized
// context value below never has to change because of them.
function useStableCallback<A extends unknown[], R>(fn: (...args: A) => R) {
  const ref = React.useRef(fn);
  React.useLayoutEffect(() => {
    ref.current = fn;
  });
  return React.useCallback((...args: A) => ref.current(...args), []);
}

function getFilter(
  namespace: string,
  preferences: Preferences,
  defaultFilter: GanttFilter,
): GanttFilter {
  try {
    return preferences[`${namespace}:ganttFilter` as keyof typeof preferences]
      ? JSON.parse(
          preferences[`${namespace}:ganttFilter` as keyof typeof preferences],
        )
      : defaultFilter;
  } catch (e) {
    return defaultFilter;
  }
}

// Store updates every 12 columns. Paired contract with OVERSCAN = 24 in
// ./calendar — the rendered window must always extend past both viewport
// edges by more than one CHUNK, so a store update is never urgent.
const CHUNK = 12;

function Gantt({
  projects,
  range,
  filter: defaultFilter,
  sort: defaultSort,
  namespace,
  isFetchingProjects,
  canRetrieveProjects,
  retrieveProjects,
  selectSort,
}: Props) {
  const { t } = useTranslation();
  const hasInitializedZoom = React.useRef(false);
  const { preferences } = useRetrievePreferences();
  const { editPreferences } = useEditPreferences();
  const { exportData } = useExportData();
  const { account } = useAuthentication();
  const { planzone } = useRetrieveCurrentPlanzone();
  const savedSort: GanttSort = (preferences[
    `${namespace}:ganttSort` as keyof typeof preferences
  ] || defaultSort) as GanttSort;
  const sort = savedSort === "zone" ? "ascendingRank" : savedSort;
  const setSort = (nextSort: S) =>
    editPreferences({ ...preferences, [`${namespace}:ganttSort`]: nextSort });
  const filter = getFilter(namespace, preferences, defaultFilter);
  const setFilter = (nextFilter: GanttFilter) =>
    editPreferences({
      ...preferences,
      [`${namespace}:ganttFilter`]: JSON.stringify(nextFilter),
    });
  const [scrollPosStore] = React.useState(() => createScrollPosStore(0));
  // Hover lives outside React: mouseEnter/mouseLeave writes to this store and
  // only the rows/bars whose isHovered boolean flips re-render (≤4), instead
  // of this whole component tree.
  const [hoverStore] = React.useState(createHoverStore);
  const graphRef = React.useRef(null);
  // Pre-shift the visible range by ±1 month. This compensates getDates'
  // internal +1 day off-by-one — see audit §11. The two are a paired
  // contract; do not collapse one without the other.
  // Memoized DateTime[] feeds ruler, positioning, and workloads consumers.
  // Amortizes the per-cell Luxon parse cost — one parse per visible day,
  // not per render-per-cell.
  const datesDt = React.useMemo(
    () =>
      getDates(
        DateTime.fromISO(range.minDate)
          .minus({ months: 1 })
          .startOf("day")
          .toISO() ?? "",
        DateTime.fromISO(range.maxDate)
          .plus({ months: 1 })
          .startOf("day")
          .toISO() ?? "",
      ).map((d) => DateTime.fromJSDate(d)),
    [range.minDate, range.maxDate],
  );
  const views = useViews();
  const workloads = useWorkloads();
  const delays = useDelays();
  const derivations = useDerivations();

  // Identity-stable versions of the api-hook setters (see useStableCallback).
  // If TypeScript flags any of these fields as missing, it just lives in a
  // different api hook than assumed — move the read, the pattern is the same.
  const selectView = useStableCallback(views.selectView);
  const selectDetailsView = useStableCallback(views.selectDetailsView);
  const toggleWorkloads = useStableCallback(workloads.toggleWorkloads);
  const toggleWorkloadsGroups = useStableCallback(
    workloads.toggleWorkloadsGroups,
  );
  const toggleDelays = useStableCallback(delays.toggleDelays);
  const toggleDerivations = useStableCallback(derivations.toggleDerivations);

  const { gantt } = useRetrieveOptimizedGantt(filter, sort, 25);
  const {
    meta,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveWbsMeta,
  } = useRetrieveWbsMeta(projects);
  const { resources } = useResources();
  const { workLoads, isRequestPending: isLoadingWorkloads } =
    useRetrieveWorkLoads({
      ...(resources.length > 0
        ? {
            resources: resources,
          }
        : {}),
      view: (views.currentView === Views.YEARLY
        ? "year"
        : views.currentView) as RetrieveWorkLoadsFilter["view"],
      startDate: datesDt[0].toUTC().toISO() ?? "",
      endDate: datesDt[datesDt.length - 1].toUTC().toISO() ?? "",
    });
  const { constraints } = useRetrieveConstraints();
  const [areDetailsOpen, setAreDetailsOpen] = React.useState(false);
  const [panelWidth, setPanelWidth] = React.useState<number>(30);
  const [elementsNbrMap, setElementsNbrMap] = React.useState<{
    [key: string]: number;
  }>({});
  const totalElementsNbr = Object.values(elementsNbrMap).reduce(
    (total, nbr) => total + nbr,
    0,
  );

  // Stable identity + functional dedupe. Returning the SAME map when the
  // count is unchanged means React bails out entirely — a child re-reporting
  // an unchanged count no longer causes a render (the old version read a
  // stale render-time snapshot of elementsNbrMap for its guard, and its
  // identity changed every render).
  const setProjectElementsNbr = React.useCallback(
    (id: string, nbr: number) =>
      setElementsNbrMap((m) => (m[id] === nbr ? m : { ...m, [id]: nbr })),
    [],
  );

  const { rangeRef, selectThumb } = useRange(panelWidth, setPanelWidth);

  const wbs = React.useRef<HTMLDivElement | null>(null);
  const graph = React.useRef<HTMLDivElement | null>(null);
  const background = React.useRef<HTMLDivElement | null>(null);
  const workloadsCal = React.useRef<HTMLDivElement | null>(null);
  const resourcesRef = React.useRef<HTMLDivElement | null>(null);
  const horizontalScrollBar = React.useRef<HTMLDivElement | null>(null);
  const paddingTop =
    views.cell.height +
    1 +
    (views.currentView === Views.DAILY
      ? views.cell.height * 2
      : views.cell.height + views.cell.height * (1 / 3));

  // Zoom changes cell.width; the same scrollLeft now maps to a different chunk
  // index. Re-derive before paint so Calendar's chunk window stays aligned.
  React.useLayoutEffect(() => {
    if (graph.current) {
      scrollPosStore.set(
        Math.round(graph.current.scrollLeft / views.cell.width),
      );
    }
  }, [views.cell.width, scrollPosStore]);

  React.useEffect(() => {
    !isFetchingProjects &&
      !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveWbsMeta({ projects });
  }, [isFetchingProjects]);

  React.useEffect(() => {
    selectSort && selectSort(savedSort);
  }, [savedSort, selectSort]);

  React.useEffect(() => {
    if (hasInitializedZoom.current || range.minDate === range.maxDate) {
      return;
    }

    const minShifted = DateTime.fromISO(range.minDate)
      .minus({ months: 1 })
      .startOf("day");
    const maxShifted = DateTime.fromISO(range.maxDate)
      .plus({ months: 1 })
      .startOf("day");
    const dateDiff = maxShifted.diff(minShifted, "days").days;

    views.selectView(
      dateDiff > 365
        ? Views.YEARLY
        : dateDiff > 31
          ? Views.MONTHLY
          : Views.WEEKLY,
    );
    const cell =
      dateDiff > 365
        ? defaultViews.yearly
        : dateDiff > 31
          ? defaultViews.monthly
          : defaultViews.weekly;
    if (background.current) {
      const width = background.current.clientWidth;
      const ganttDoesNotFit = cell.width * dateDiff > width;
      const calculateZoomToFit = (
        baseCellWidth: number,
        dateDiff: number,
        containerWidth: number,
      ): number => {
        if (baseCellWidth === 0 || dateDiff === 0) return 0;

        const targetCellWidth = containerWidth / dateDiff;
        const zoom = 10 * (targetCellWidth / baseCellWidth - 1);

        return Math.round(Math.max(zoom, -9.5));
      };

      ganttDoesNotFit &&
        views.setZoom(calculateZoomToFit(cell.width, dateDiff, width));

      hasInitializedZoom.current = true;
    }
  }, [range.minDate, range.maxDate]);

  useMessage(
    ({ type }) =>
      (type === events.moveListElementSucceeded ||
        ["Task", "List", "Milestone", "Event"].find(
          (elementType) =>
            type === `create${elementType}Succeeded` ||
            type === `remove${elementType}Succeeded`,
        ) ||
        type === events.editAllocationSucceeded ||
        type === events.removeAllocationSucceeded ||
        type === events.allocateResourcesSucceeded) &&
      retrieveWbsMeta({ projects }),
  );

  // The value's identity now only changes when one of the scalar deps flips
  // (zoom, view, a toggle). A Gantt render caused by data, hover, or element
  // counts no longer invalidates every useContext(Context) consumer. All the
  // callbacks included below are identity-stable, so listing them in deps is
  // free and keeps the exhaustive-deps lint clean.
  const ctxValue = React.useMemo(
    () => ({
      cell: { height: views.cell.height, width: views.cell.width },
      currentView: views.currentView,
      detailsView: views.detailsView,
      areDetailsOpen: views.areDetailsOpen,
      selectView,
      selectDetailsView,

      areWorkloadsToggled: workloads.areWorkloadsToggled,
      toggleWorkloads,
      areWorkloadsGroupsToggled: workloads.areWorkloadsGroupsToggled,
      toggleWorkloadsGroups,

      areDelaysToggled: delays.areDelaysToggled,
      toggleDelays,

      areDerivationsToggled: derivations.areDerivationsToggled,
      toggleDerivations,

      setProjectElementsNbr,
    }),
    [
      views.cell.height,
      views.cell.width,
      views.currentView,
      views.detailsView,
      views.areDetailsOpen,
      workloads.areWorkloadsToggled,
      workloads.areWorkloadsGroupsToggled,
      delays.areDelaysToggled,
      derivations.areDerivationsToggled,
      selectView,
      selectDetailsView,
      toggleWorkloads,
      toggleWorkloadsGroups,
      toggleDelays,
      toggleDerivations,
      setProjectElementsNbr,
    ],
  );

  return (
    <ScrollPosProvider value={scrollPosStore}>
      <HoverProvider value={hoverStore}>
        <Context.Provider value={ctxValue}>
          <TreeToggle>
            <div className={styles.gantt}>
              <div className={styles.section}>
                <ScrollRoot>
                  <InfiniteScrollRoot canFetchOnIdle>
                    <div
                      className={styles.layer}
                      ref={background}
                      style={{
                        left: `${panelWidth}%`,
                        overflow: "hidden",
                      }}
                    >
                      <Calendar
                        dates={datesDt}
                        cell={views.cell}
                        currentView={views.currentView}
                        height={totalElementsNbr * views.cell.height}
                      />
                    </div>
                    <div className={styles.layer}>
                      <div
                        ref={rangeRef}
                        className={classNames(styles.grid, {
                          [styles.workloads]: workloads.areWorkloadsToggled,
                        })}
                        style={{
                          gridTemplateColumns: `${panelWidth}% 1fr`,
                        }}
                      >
                        <div className={styles.cell}>
                          <ScrollZone
                            id="projectElements"
                            orientation="vertical"
                          >
                            <ScrollZone id="wbs" orientation="horizontal">
                              <ScrollBar
                                id="wbs"
                                scrollSize={areDetailsOpen ? 720 : 460}
                                orientation="horizontal"
                                scroll={(scrollPos) => {
                                  if (wbs.current) {
                                    wbs.current.scrollLeft = scrollPos;
                                  }
                                }}
                              >
                                <div
                                  ref={wbs}
                                  id="projectGantt"
                                  className={styles.leftPanel}
                                >
                                  <div
                                    style={{
                                      minHeight:
                                        totalElementsNbr * views.cell.height +
                                        paddingTop +
                                        45,
                                    }}
                                  >
                                    <WbsHeader
                                      isOpen={areDetailsOpen}
                                      toggle={() => {
                                        setPanelWidth(
                                          !areDetailsOpen
                                            ? panelWidth + 30
                                            : panelWidth - 30,
                                        );
                                        setAreDetailsOpen(!areDetailsOpen);
                                      }}
                                    />
                                    <div
                                      className={classNames(styles.row, {
                                        [styles.openedDetails]: areDetailsOpen,
                                      })}
                                    >
                                      <div className={styles.wbs}>
                                        <Wbs
                                          projects={projects}
                                          filter={filter}
                                          sort={sort}
                                          gantt={gantt}
                                          meta={meta}
                                          style={{
                                            itemHeight: 30,
                                            colWidth: "4.5rem",
                                            indentSize: "3rem",
                                          }}
                                          loaderId="projectGantt"
                                          configuration={[
                                            { key: "allocations" },
                                          ]}
                                          isFetchingProjects={
                                            isFetchingProjects
                                          }
                                        />
                                        <Flag
                                          name="projects"
                                          isDisabled={!canRetrieveProjects}
                                          retrieveItems={retrieveProjects}
                                        />
                                      </div>
                                      {areDetailsOpen && (
                                        <DetailsView
                                          projects={projects}
                                          filter={filter}
                                          sort={sort}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </ScrollBar>
                            </ScrollZone>
                          </ScrollZone>
                        </div>
                        <div className={styles.cell}>
                          <div className={styles.zoomMenu}>
                            <ZoomMenu
                              zoom={views.zoom}
                              zoomIn={() => views.setZoom(views.zoom + 1)}
                              zoomOut={() =>
                                views.setZoom(
                                  views.zoom <= 0
                                    ? Math.max(views.zoom - 0.5, -9.5)
                                    : views.zoom - 1,
                                )
                              }
                            />
                          </div>
                          <ScrollZone id="graph" orientation="horizontal">
                            <ScrollBar
                              id="projectElements"
                              orientation="vertical"
                              scrollSize={
                                totalElementsNbr * views.cell.height +
                                paddingTop +
                                45
                              }
                              withInfiniteLoad
                              scroll={(scrollPos) => {
                                if (wbs.current) {
                                  wbs.current.scrollTop = scrollPos;
                                }

                                if (graph.current) {
                                  graph.current.scrollTop = scrollPos;
                                }
                              }}
                            >
                              <ScrollBar
                                id="graph"
                                orientation="horizontal"
                                scrollSize={datesDt.length * views.cell.width}
                                scroll={(scrollPos) => {
                                  if (background.current)
                                    background.current.scrollLeft = scrollPos;
                                  if (workloadsCal.current)
                                    workloadsCal.current.scrollLeft = scrollPos;
                                  if (graph.current)
                                    graph.current.scrollLeft = scrollPos;
                                  scrollPosStore.set(
                                    Math.floor(
                                      scrollPos / views.cell.width / CHUNK,
                                    ) * CHUNK, // no rAF
                                  );
                                }}
                                updateRef={(ref) => {
                                  horizontalScrollBar.current = ref.current;
                                }}
                              >
                                <ScrollZone
                                  id="projectElements"
                                  orientation="vertical"
                                >
                                  <div
                                    ref={graph}
                                    className={styles.graph}
                                    style={{
                                      marginTop: paddingTop,
                                      minHeight: `calc(100% - ${paddingTop + 30}px)`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        minHeight:
                                          totalElementsNbr * views.cell.height +
                                          paddingTop +
                                          45,
                                        marginTop: (paddingTop + 45) * -1,
                                        height: 0,
                                      }}
                                    >
                                      <Graph
                                        ref={graphRef}
                                        gantt={gantt}
                                        filter={filter}
                                        sort={sort}
                                        constraints={constraints}
                                        dates={datesDt}
                                        projects={projects}
                                      />
                                    </div>
                                  </div>
                                </ScrollZone>
                              </ScrollBar>
                            </ScrollBar>
                          </ScrollZone>
                        </div>
                        {workloads.areWorkloadsToggled && (
                          <>
                            <div className={styles.cell}>
                              <ScrollZone id="workloads" orientation="vertical">
                                <ScrollZone
                                  id="resources"
                                  orientation="horizontal"
                                >
                                  <ScrollBar
                                    id="resources"
                                    orientation="horizontal"
                                    scrollSize={460}
                                    scroll={(scrollPos) => {
                                      if (resourcesRef.current) {
                                        resourcesRef.current.scrollLeft =
                                          scrollPos;
                                      }
                                    }}
                                  >
                                    <div
                                      ref={resourcesRef}
                                      className={styles.leftPanel}
                                    >
                                      <Resources
                                        filter={filter}
                                        workLoads={workLoads}
                                        isLoading={isLoadingWorkloads}
                                      />
                                    </div>
                                  </ScrollBar>
                                </ScrollZone>
                              </ScrollZone>
                            </div>
                            <div className={styles.cell}>
                              <ScrollZone id="workloads" orientation="vertical">
                                <ScrollZone id="graph" orientation="horizontal">
                                  <ScrollBar
                                    id="workloads"
                                    orientation="vertical"
                                    className={styles.workLoadsRoot}
                                    scrollSize={
                                      workLoads.length * views.cell.height
                                    }
                                    scroll={(scrollPos) => {
                                      if (resourcesRef.current) {
                                        resourcesRef.current.scrollTop =
                                          scrollPos;
                                      }

                                      if (workloadsCal.current) {
                                        workloadsCal.current.scrollTop =
                                          scrollPos;
                                      }
                                    }}
                                  >
                                    <div
                                      ref={workloadsCal}
                                      className={styles.graph}
                                    >
                                      <div className={styles.header}>
                                        <div className={styles.row}></div>
                                        <div className={styles.row}>
                                          <p className={styles.label}>
                                            {t("workLoads.labels.capacity")}
                                          </p>
                                        </div>
                                      </div>
                                      <WorkLoads
                                        dates={datesDt}
                                        filter={filter}
                                      />
                                    </div>
                                  </ScrollBar>
                                </ScrollZone>
                              </ScrollZone>
                            </div>
                          </>
                        )}

                        <div
                          className={styles.track}
                          style={{ left: `calc(${panelWidth}% + 0.5px)` }}
                        >
                          <div
                            className={styles.thumb}
                            onMouseDown={selectThumb}
                            onTouchStart={selectThumb}
                          >
                            <svg
                              className={styles.thumbIcon}
                              viewBox="0 0 7 14"
                            >
                              <line
                                y2="12"
                                transform="translate(6 1)"
                                strokeLinecap="round"
                                strokeWidth="2"
                              />
                              <line
                                y2="12"
                                transform="translate(1 1)"
                                strokeLinecap="round"
                                strokeWidth="2"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </InfiniteScrollRoot>
                </ScrollRoot>
              </div>
              <Header
                project={projects.length === 1 ? projects[0] : undefined}
                gantt={gantt}
                ganttLength={totalElementsNbr}
                filter={filter}
                sort={savedSort}
                moveToToday={() => {
                  const todayIndex = datesDt.findIndex(isToday);

                  horizontalScrollBar.current &&
                    horizontalScrollBar.current.scrollTo({
                      top: 0,
                      left: todayIndex * views.cell.width,
                      behavior: "smooth",
                    });
                }}
                setFilter={setFilter}
                setSort={setSort}
                reset={() => {
                  editPreferences({
                    ...preferences,
                    [`${namespace}:ganttFilter`]: JSON.stringify(defaultFilter),
                    [`${namespace}:ganttSort`]: defaultSort,
                  });
                }}
              />
              <div className={styles.exportButton}>
                <ExportButton
                  export={(toggledLists, ganttType) =>
                    exportData({
                      fileName: planzone.name,
                      author: account,
                      data: {
                        type: ExportsDataTypes.OPTIMIZED_GANTT_REPORT,
                        data: {
                          planzone: planzone,
                          // ExportsData.dateRange is typed [Date, Date];
                          // convert at the boundary until the export DTO is
                          // migrated.
                          dateRange: [
                            DateTime.fromISO(range.minDate).toJSDate(),
                            DateTime.fromISO(range.maxDate).toJSDate(),
                          ],
                          projects: projects,
                          toggledLists,
                          filter,
                          sort,
                          ganttType,
                        },
                      },
                      format: ExportsFormats.EXCEL,
                    })
                  }
                />
              </div>
            </div>
          </TreeToggle>
        </Context.Provider>
      </HoverProvider>
    </ScrollPosProvider>
  );
}

export { Gantt };
